import React, { useState, useEffect } from 'react';
import { useStateContext } from '../../contexts/ContextProvider';
import { getCurrentUser, signOut } from '../../lib/appwrite';
import FormInput from '../../components/FormField';
import { Button } from '../../components';

const Account = () => {
  const { account, setAccount, setIsLoggedIn } = useStateContext();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (account) {
      setUsername(account.username || '');
      setEmail(account.email || '');
    }
  }, [account]);

  const handleUpdateAccount = async () => {
    setError('');
    setSuccess('');
    setIsLoading(true);

    try {
      // Validate inputs
      if (!username || !email) {
        setError('Username and email are required');
        setIsLoading(false);
        return;
      }

      if (newPassword && newPassword !== confirmPassword) {
        setError('New passwords do not match');
        setIsLoading(false);
        return;
      }

      if (newPassword && !currentPassword) {
        setError('Current password is required to change password');
        setIsLoading(false);
        return;
      }

      // Prepare update data
      const updateData = {
        username,
        email,
      };

      // If password is being changed, include it
      if (newPassword) {
        updateData.currentPassword = currentPassword;
        updateData.newPassword = newPassword;
      }

      // Make API call to update account
      const API_BASE_URL = process.env.REACT_APP_API_URL || '';
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_BASE_URL}/api/users/me`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(updateData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to update account');
      }

      const updatedUser = await response.json();
      setAccount(updatedUser);
      setSuccess('Account updated successfully');
      
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      // If password was changed, sign out and require re-login
      if (newPassword) {
        setTimeout(() => {
          signOut();
          setIsLoggedIn(false);
          window.location.href = '/';
        }, 2000);
      }
    } catch (err) {
      setError(err.message || 'Failed to update account');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto">
        <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-6">Account Settings</h1>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 space-y-6">
          {error && (
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {success && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 text-green-700 dark:text-green-400 px-4 py-3 rounded">
              {success}
            </div>
          )}

          <div className="space-y-4">
            <FormInput
              title="Username"
              placeholder="Username"
              value={username}
              handleChangeText={setUsername}
              required
            />

            <FormInput
              title="Email"
              placeholder="Email"
              value={email}
              handleChangeText={setEmail}
              required
            />

            <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
              <h2 className="text-lg font-semibold text-gray-800 dark:text-white mb-4">Change Password</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                Leave blank if you don't want to change your password
              </p>

              <div className="space-y-4">
                <FormInput
                  title="Current Password"
                  placeholder="Enter current password"
                  value={currentPassword}
                  handleChangeText={setCurrentPassword}
                  type="password"
                />

                <FormInput
                  title="New Password"
                  placeholder="Enter new password"
                  value={newPassword}
                  handleChangeText={setNewPassword}
                  type="password"
                />

                <FormInput
                  title="Confirm New Password"
                  placeholder="Confirm new password"
                  value={confirmPassword}
                  handleChangeText={setConfirmPassword}
                  type="password"
                />
              </div>
            </div>

            <div className="pt-4">
              <Button
                bgColor="#1E4DB7"
                text="Update Account"
                borderRadius="10px"
                color="white"
                isLoading={isLoading}
                onClick={handleUpdateAccount}
              />
            </div>
          </div>
        </div>
      </div>
  );
};

export default Account;

